import {
	AnalyticsTotalStatsDto,
	EngagementsChartDto,
	EngagementStatusByLanguageDto,
	LanguageDistributionDto,
	LanguagePerProvidersDto,
	SurveyEngagementFunnelDto,
	SurveyTrendOverTimeDto,
	AnalyticsBaseQueryParams,
	AnalyticsEngagementsQueryParams,
	AnalyticsProvidersQueryParams,
	TotalLanguagesRowDto,
	PublicLinkDataRequestDto,
	PublicLinkDataResponseDto,
	DecryptPublicLinkDataResponseDto,
	DecryptPublicLinkDataRequestDto,
	AuthenticatedQueryParams,
	AnalyticsTotalLanguagesQueryParams,
} from '@families-link/shared';
import api from '.';

const analyticsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getPublicLinkData: builder.query<PublicLinkDataResponseDto, PublicLinkDataRequestDto>({
			query: ({ filters }) => ({
				url: `analytics/public-link`,
				method: 'POST',
				body: { filters },
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		decodePublicLinkData: builder.query<DecryptPublicLinkDataResponseDto, DecryptPublicLinkDataRequestDto>({
			query: ({ data }) => ({
				url: 'analytics/public-link/params',
				method: 'POST',
				body: {
					data,
				},
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		getAnalyticsLanguageDistribution: builder.query<
			LanguageDistributionDto[],
			AuthenticatedQueryParams<AnalyticsBaseQueryParams>
		>({
			query: ({ queryParams, authToken }) => ({
				url: `analytics/engagements/languages`,
				params: queryParams,
				headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		getAnalyticsEngagementsChartData: builder.query<
			EngagementsChartDto[],
			AuthenticatedQueryParams<AnalyticsEngagementsQueryParams>
		>({
			query: ({ queryParams, authToken }) => ({
				url: `analytics/engagements`,
				params: queryParams,
				headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		getAnalyticsProviders: builder.query<
			LanguagePerProvidersDto[],
			AuthenticatedQueryParams<AnalyticsProvidersQueryParams>
		>({
			query: ({ queryParams, authToken }) => ({
				url: `analytics/providers`,
				params: queryParams,
				headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		getSurveyEngagementFunnel: builder.query<
			SurveyEngagementFunnelDto[],
			AuthenticatedQueryParams<AnalyticsBaseQueryParams>
		>({
			query: ({ queryParams, authToken }) => ({
				url: `analytics/surveys/engagement-funnel`,
				params: queryParams,
				headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		getEngagementStatusByLanguage: builder.query<
			EngagementStatusByLanguageDto[],
			AuthenticatedQueryParams<AnalyticsBaseQueryParams>
		>({
			query: ({ queryParams, authToken }) => ({
				url: `analytics/engagements/statuses/languages`,
				params: queryParams,
				headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		getAnalyticsSurveysTrendOverTime: builder.query<
			SurveyTrendOverTimeDto[],
			AuthenticatedQueryParams<AnalyticsBaseQueryParams>
		>({
			query: ({ queryParams, authToken }) => ({
				url: `analytics/surveys/trend-over-time`,
				params: queryParams,
				headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		getAnalyticsTotalStatistics: builder.query<
			AnalyticsTotalStatsDto,
			AuthenticatedQueryParams<AnalyticsBaseQueryParams>
		>({
			query: ({ queryParams, authToken }) => ({
				url: `analytics/total-statistics`,
				params: queryParams,
				headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		getAnalyticsTotalLanguages: builder.query<TotalLanguagesRowDto[], AnalyticsTotalLanguagesQueryParams>({
			query: ({ queryParams, sorting, authToken }) => ({
				url: 'analytics/total-languages-modal-data',
				params: { ...queryParams, ...sorting },
				headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
	}),
});

export default analyticsApi;

export const {
	useLazyGetPublicLinkDataQuery,
	useDecodePublicLinkDataQuery,
	useGetAnalyticsLanguageDistributionQuery,
	useGetAnalyticsEngagementsChartDataQuery,
	useGetAnalyticsProvidersQuery,
	useGetSurveyEngagementFunnelQuery,
	useGetEngagementStatusByLanguageQuery,
	useGetAnalyticsSurveysTrendOverTimeQuery,
	useGetAnalyticsTotalStatisticsQuery,
	useGetAnalyticsTotalLanguagesQuery,
} = analyticsApi;
